import React from 'react';

import { makeUclComponent } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import Link from 'components/link';
import { routes } from 'navigation/routing';
import { useUIContext } from 'state/ui';
import { platform } from 'utils/environment';

import { useActiveTieredDeliveryFees } from '../hooks/use-active-tiered-delivery-fees';

import { BodyContainer, ItemContainer, SubtitleContainer } from './styled';
import { IDeliveryFeesModalProps } from './types';

const TermsLink = makeUclComponent(Link).withConfig({
  color: Styles.color.primary,
});

const DeliveryFeesModal: React.FC<React.PropsWithChildren<IDeliveryFeesModalProps>> = ({
  open,
  onClose,
  subTotalCents,
  deliveryFeeCents,
  smallCartFeeCents,
  serviceFeeCents,
  geographicalFeeCents,
  isLoading,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { smallCartFeeThreshold, tier } = useActiveTieredDeliveryFees({
    subTotalCents,
    platform: platform(),
  });
  const { formatMessage } = useIntl();
  if (!open) {
    return null;
  }
  return (
    <Dialog
      showDialog={open}
      testID="delivery-fees-modal"
      heading={formatMessage({
        id: 'deliveryFees',
      })}
      bodyComponent={
        <BodyContainer>
          <ItemContainer testID="delivery-fee-item">
            <SubtitleContainer>
              {formatMessage({
                id: 'deliveryFee',
              })}
              {': '}
              <Currency amount={deliveryFeeCents} isLoading={isLoading} />
            </SubtitleContainer>
            {formatMessage(
              { id: 'deliveryFeeDescription' },
              {
                link: (
                  <TermsLink to={routes.deliveryTerms}>{formatMessage({ id: 'here' })}</TermsLink>
                ),
              }
            )}
          </ItemContainer>
          {geographicalFeeCents > 0 && (
            <ItemContainer testID="geographical-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'geographicalFee',
                })}
                {': '}
                <Currency amount={geographicalFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {formatMessage(
                {
                  id: 'geographicalFeeDescription',
                },
                {
                  geographicalFeeAmount: centsToDollarString(geographicalFeeCents),
                }
              )}
            </ItemContainer>
          )}
          {serviceFeeCents > 0 && (
            <ItemContainer testID="service-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'serviceFee',
                })}
                {': '}
                <Currency amount={serviceFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {formatMessage(
                {
                  id: 'serviceFeeDescription',
                },
                {
                  percent: tier.serviceFeePercent,
                }
              )}
            </ItemContainer>
          )}
          {smallCartFeeCents > 0 && (
            <ItemContainer testID="small-cart-fee-item">
              <SubtitleContainer>
                {formatMessage({
                  id: 'smallCartFee',
                })}
                {': '}
                <Currency amount={smallCartFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {smallCartFeeThreshold > 0 &&
                formatMessage(
                  {
                    id: 'smallCartFeeDescription',
                  },
                  {
                    amount: formatCurrencyForLocale(smallCartFeeThreshold),
                  }
                )}
            </ItemContainer>
          )}
        </BodyContainer>
      }
      actions={
        <ActionButton
          onPress={onClose}
          testID="delivery-fees-modal-ok-btn"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {formatMessage({
            id: 'okayButtonText',
          })}
        </ActionButton>
      }
      modalAppearanceEventMessage="Delivery Fees Modal"
      onDismiss={onClose}
    />
  );
};

export default DeliveryFeesModal;
